'use client';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as React from 'react';

import { cn } from '../utils';

import { Label } from './Label';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    data-slot="radio-group"
    ref={ref}
    className={cn('grid gap-3 text-md', className)}
    {...props}
  />
));
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    data-slot="radio-group-item"
    ref={ref}
    className={cn(
      'border-input text-primary focus-visible:border-ring aspect-square size-5 shrink-0 rounded-full border data-[state=checked]:border-primary data-[state=checked]:bg-primary',
      className,
    )}
    {...props}
  >
    <RadioGroupPrimitive.Indicator
      data-slot="radio-group-indicator"
      className="flex items-center justify-center"
    >
      <span className="block rounded-full bg-white size-2" />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
));
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

const RadioGroupOption = ({
  className,
  label,
  labelProps,
  id,
  disabled,
  ...props
}: React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
  id: string;
  label: React.ReactNode;
  labelProps?: React.ComponentPropsWithoutRef<typeof Label>;
}) => {
  return (
    <div
      className={cn(
        'flex items-center space-x-2',
        disabled && 'opacity-40',
        className,
      )}
      aria-disabled={disabled}
    >
      <RadioGroupItem id={id} disabled={disabled} {...props} />
      <RadioGroupLabel htmlFor={id} {...labelProps}>
        {label}
      </RadioGroupLabel>
    </div>
  );
};

const RadioGroupCardOption = ({
  label,
  description,
  className,
  id,
  disabled,
  ...props
}: React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
  label: React.ReactNode;
  description?: React.ReactNode;
}) => {
  return (
    <label
      className={cn(
        'flex flex-col gap-2 border border-border rounded-sm bg-background p-4',
        disabled && 'opacity-40',
        className,
      )}
    >
      <div className="flex items-center space-x-2">
        <RadioGroupItem id={id} disabled={disabled} {...props} />
        <RadioGroupLabel>{label}</RadioGroupLabel>
      </div>
      {description ? (
        <div className="flex flex-col">
          <RadioGroupDescription>{description}</RadioGroupDescription>
        </div>
      ) : null}
    </label>
  );
};

const RadioGroupLabel = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof Label>) => (
  <Label
    data-slot="radio-group-label"
    className={cn('text-md font-normal text-foreground', className)}
    {...props}
  />
);

const RadioGroupDescription = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'p'>) => (
  <p
    data-slot="radio-group-description"
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
);

const RadioGroupTitle = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'p'>) => (
  <p
    data-slot="radio-group-title"
    className={cn('mb-3 font-medium text-foreground', className)}
    {...props}
  />
);

export {
  RadioGroup,
  RadioGroupItem,
  RadioGroupCardOption,
  RadioGroupLabel,
  RadioGroupOption,
  RadioGroupDescription,
  RadioGroupTitle,
};
